import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { HlmIconComponent } from '@lite-workspace/shared/helm-ui/icon';
import { NotificationsStore } from './notifications.store';
import { NotificationRowComponent, isRunDone } from './notification-row.component';
import { NOTIFICATIONS_HOST_PRODUCT, resolveNotificationNavigation, type NotificationItem } from './notifications.models';

const POLL_INTERVAL_MS = 60_000;

// The top-bar bell (spec 17 §2): unread badge + a dropdown quick view of the
// latest few events, with the feed and settings one click away. The unread count
// polls while the shell lives and refreshes eagerly on every open.
// One block for every cabinet: the only thing a host supplies is its own inbox
// product tag, plus the /notifications routes the two links below expect.
@Component({
  selector: 'app-notifications-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmDropdownMenuImports, HlmIconComponent, RouterLink, NotificationRowComponent],
  template: `
    <button
      type="button"
      [hlmDropdownMenuTrigger]="menu"
      #trigger="hlmDropdownMenuTrigger"
      align="end"
      side="bottom"
      aria-label="Уведомления"
      class="relative grid size-7 place-items-center rounded-full border border-line2 bg-panel3 text-c2"
      (click)="onOpen()"
    >
      <hlm-icon src="/assets/icons/bell.svg" [size]="16" />
      @if (badge(); as label) {
        <span
          class="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-acc px-1
            text-[10px] font-semibold leading-none text-on-acc"
          >{{ label }}</span
        >
      }
    </button>

    <ng-template #menu>
      <div hlmDropdownMenu class="w-[340px] rounded-[var(--r)] border border-line bg-panel2 p-[10px] shadow-[var(--shadow-3)]">
        <div class="flex items-center justify-between px-2 pb-2 pt-1">
          <span class="text-sm font-semibold text-ink">Уведомления</span>
          <span class="flex items-center gap-1">
            @if (store.unreadCount() > 0) {
              <button
                type="button"
                class="cursor-pointer rounded-[var(--r-s)] px-2 py-1 text-xs text-c2 hover:bg-panel3 hover:text-ink"
                (click)="store.markAllRead()"
              >
                Прочитать все
              </button>
            }
            <a
              routerLink="/notifications/settings"
              (click)="trigger.close()"
              aria-label="Настройки уведомлений"
              class="grid size-6 place-items-center rounded-[var(--r-s)] text-c3 hover:bg-panel3 hover:text-ink"
            >
              <hlm-icon src="/assets/icons/gear.svg" [size]="14" />
            </a>
          </span>
        </div>

        @switch (store.recentStatus()) {
          @case ('loading') {
            <p class="px-3 py-6 text-center text-sm text-c3">Загрузка…</p>
          }
          @case ('error') {
            <p class="px-3 py-6 text-center text-sm text-c3">Не удалось загрузить уведомления.</p>
          }
          @default {
            @if (store.recent().length === 0) {
              <p class="px-3 py-6 text-center text-sm text-c3">Пока нет уведомлений.</p>
            } @else {
              @for (item of store.recent(); track item.id) {
                <!-- Rows and the feed link are not menu items, so CDK leaves the menu open on navigation. -->
                <app-notification-row [item]="item" [compact]="true" (activate)="trigger.close(); onActivate(item)" />
              }
            }
          }
        }

        <a
          routerLink="/notifications"
          (click)="trigger.close()"
          class="mt-1 block rounded-[var(--r-s)] px-3 py-[10px] text-center text-sm font-semibold text-acc2
            hover:bg-panel3"
          >Все уведомления</a
        >
      </div>
    </ng-template>
  `,
})
export class NotificationsBellComponent {
  protected readonly store = inject(NotificationsStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostProduct = inject(NOTIFICATIONS_HOST_PRODUCT, { optional: true }) ?? '';

  protected readonly badge = computed(() => {
    const count = this.store.unreadCount();
    if (count <= 0) return null;
    return count > 9 ? '9+' : String(count);
  });

  constructor() {
    void this.store.refreshUnreadCount();
    const timer = setInterval(() => void this.store.refreshUnreadCount(), POLL_INTERVAL_MS);
    this.destroyRef.onDestroy(() => clearInterval(timer));
  }

  protected onOpen(): void {
    void this.store.loadRecent();
    void this.store.refreshUnreadCount();
  }

  // The dropdown shows a title and a timestamp, never the body — so a row leads to the feed, where the
  // event is readable in full and its own link is one more click away. Jumping straight to the target
  // from here skipped the text entirely, and on an event whose link resolves to nothing it did nothing.
  // A finished run is whole in the row (count + project), so it opens its photos; no target → the feed.
  protected onActivate(item: NotificationItem): void {
    void this.store.markRead(item.id);
    const nav = isRunDone(item) ? resolveNotificationNavigation(item, this.hostProduct) : null;
    if (nav?.kind === 'internal') void this.router.navigateByUrl(nav.url);
    else if (nav?.kind === 'external') window.open(nav.url, '_blank', 'noopener');
    else void this.router.navigateByUrl('/notifications');
  }
}
